import { getLeadId, getUserId } from './journey';

/**
 * Stamps the current leadId into a request per the backend's per-endpoint
 * convention (body field, X-Lead-Id header, or query param).
 *
 * Per `frontend_api_guide.html` -> "Lead Identification":
 *   - Most POST/PUT endpoints expect `leadId` in the JSON body.
 *   - Some GET endpoints expect `X-Lead-Id` header.
 *   - Some GET endpoints expect `?leadId=xxx` query param.
 *
 * Caller passes the descriptor matching that endpoint's convention.
 *
 *   stampLead({ url, headers, body }, { mode: 'body' })
 *   stampLead({ url, headers, body }, { mode: 'header' })
 *   stampLead({ url, headers, body }, { mode: 'query' })
 *   stampLead({ url, headers, body }, { mode: 'none' })   // anonymous endpoints
 */
export const stampLead = (req, descriptor = {}) => {
    const mode = descriptor.mode || 'body';
    if (mode === 'none') return req;

    // Stage 3+ canonical field name is `userId` (numeric long, decoded from
    // the JWT `sub` claim); Stages 1-2 still use `leadId` (Guid). Caller
    // selects via `descriptor.field` (default 'leadId') and the source
    // is routed accordingly.
    const field = descriptor.field || 'leadId';
    const leadId = descriptor.leadId
        || (field === 'userId' ? getUserId() : getLeadId());
    if (!leadId) return req;

    if (mode === 'body') {
        let body = req.body;
        if (typeof body === 'string') {
            try {
                body = JSON.parse(body);
            } catch (e) {
                return req;
            }
        }
        // Stage 3+ `userId` must serialise as a JSON number (BE expects
        // System.Int64) — strict deserialisers reject the quoted-string form.
        // Stage 1-2 `leadId` stays a string (it's a Guid).
        const stamped = field === 'userId' && /^\d+$/.test(String(leadId))
            ? Number(leadId)
            : leadId;
        body = { ...(body || {}), [field]: stamped };
        return { ...req, body: JSON.stringify(body) };
    }

    if (mode === 'header') {
        // Header name follows the canonical BE convention:
        //   field='userId' → X-User-Id (Stage 3+ canonical)
        //   field='leadId' (default) → X-Lead-Id (Stage 1-2 legacy)
        // BE Stage 6 added a `LeadIdHeaderAliasMiddleware` that maps
        // X-Lead-Id → X-User-Id, but FE prefers the canonical header so
        // requests succeed even on BE deployments without the middleware.
        const headerName = field === 'userId' ? 'X-User-Id' : 'X-Lead-Id';
        return { ...req, headers: { ...(req.headers || {}), [headerName]: leadId } };
    }

    if (mode === 'query') {
        const sep = req.url.includes('?') ? '&' : '?';
        return { ...req, url: `${req.url}${sep}${encodeURIComponent(field)}=${encodeURIComponent(leadId)}` };
    }

    return req;
};
